import React, { useEffect, useLayoutEffect, useState } from "react";

import CardContent from "@busbud/design-system-components/dist/CardContent";
import CircularProgress from "@busbud/design-system-components/dist/CircularProgress";
import Heading from "@busbud/design-system-components/dist/Heading";
import IconButton from "@busbud/design-system-components/dist/IconButton";
import CrossMD from "@busbud/design-system-components/dist/Icons/Cross/MD";
import Stack from "@busbud/design-system-components/dist/Stack";
import { Select } from "@busbud/horizon";

import { useFetchSegmentStopsIfNotLoaded } from "@app/components/itinerary/use-fetch-trip-segment-stops";
import { useAppContext, useAppDispatch } from "@app/helpers/hooks";
import { closeDrawer } from "@app/modules/results/actions";
import { UrgencyAlert } from "@app/modules/results/components/inventory-alerts/urgency-alert/urgency-alert";
import { shouldRemoveNavigationJourneyId } from "@app/modules/results/helpers/should-remove-navigation-search-params";
import { useNavigation } from "@app/modules/results/hooks/use-navigation";
import { useFetchSegmentVehicleIfNotLoaded } from "@app/modules/seat-selection-first/hooks/use-fetch-segment-vehicle";
import { useSeatSelectionFirst } from "@app/modules/seat-selection-first/hooks/use-seat-selection-first";
import { ClassesList } from "@app/modules/ticket-options/classes";
import { FaresList } from "@app/modules/ticket-options/fares";
import { OperatorRatingsCardHeader } from "@app/modules/ticket-options/operator-ratings/operator-ratings-card-header.connect";
import { OperatorRatings } from "@app/modules/ticket-options/operator-ratings/operator-ratings.connect";
import { PriceLockSmokeTest } from "@app/modules/ticket-options/price-lock-smoke-test";

import type { ConnectorProps } from "./selection-options.connect";
import type { TicketOptionsCardName } from "./selection-options.constants";

import {
  trackTicketOptionsCardCollapse,
  trackTicketOptionsCardExpand
} from "./actions";
import { BottomSection } from "./bottom-section";
import {
  AMENITIES_CARD_NAME,
  CLASSES_AND_FARES_CARD_NAME,
  ITINERARY_CARD_NAME,
  REVIEWS_CARD_NAME,
  SHUTTLE_DESTINATIONS_CARD_NAME
} from "./selection-options.constants";
import { useStyles } from "./selection-options.styles";
import { ExpandableCardContent } from "../../card-with-header/expandable-card";
import { ExpandableCardsAccordion } from "../../expandable-cards-accordion";
import { Itinerary } from "../../itinerary";

export const SelectionOptions: React.FC<ConnectorProps> = ({
  default_trip_id,
  default_trip_by_journey_id,
  error,
  is_amenities_selection_hidden,
  is_external,
  is_loading_vehicle,
  is_vehicle_available,
  is_single_class_option,
  is_single_fare_option,
  leg_card_id,
  onClose,
  operator_has_ratings,
  trip_options_matrix,
  result_search_count,
  inventory_ready,
  stops_loaded,
  unique_destinations,
  search_trip_id
}) => {
  const { translator, features } = useAppContext();
  const { classes } = useStyles();
  const { goBack, journey_id, setJourneyId } = useNavigation();
  const { is_seat_selection_first_enabled } = useSeatSelectionFirst();
  const dispatch = useAppDispatch();
  const [selected_trip_id, selectTripId] = useState<string | undefined>();

  // If the journey_id can't be matched, remove it from the URL
  useEffect(() => {
    if (
      shouldRemoveNavigationJourneyId(
        journey_id,
        default_trip_by_journey_id,
        inventory_ready
      )
    ) {
      setJourneyId(null);
    }
  }, [journey_id, default_trip_by_journey_id, inventory_ready, setJourneyId]);

  const active_trip_id: string | undefined =
    inventory_ready && trip_options_matrix.length
      ? selected_trip_id || search_trip_id || default_trip_id
      : undefined;

  const is_itinerary_block_expanded =
    (is_single_class_option && is_single_fare_option) ||
    !!features.ENABLE_SHUTTLE_CARD;
  const is_comfort_block_expanded =
    !is_single_class_option || is_single_fare_option;

  useFetchSegmentVehicleIfNotLoaded(
    active_trip_id ? [active_trip_id] : [],
    !is_seat_selection_first_enabled || is_external
  );
  useFetchSegmentStopsIfNotLoaded(stops_loaded, default_trip_id);

  useLayoutEffect(() => {
    const is_trip_not_found = inventory_ready && !active_trip_id;
    if (error || is_trip_not_found) {
      dispatch(closeDrawer());
      goBack(translator.t("!error.CartDepartureExpired.text"));
    }
  }, [error, translator, inventory_ready, dispatch, goBack, active_trip_id]);

  const renderLoader = () => <CircularProgress className={classes.loader} />;

  function generateTrackingEvents(
    trip_id: string | undefined,
    card_name: TicketOptionsCardName
  ) {
    if (!trip_id) {
      return {};
    }

    return {
      onCollapse: () => {
        dispatch(trackTicketOptionsCardCollapse(trip_id, card_name));
      },
      onExpand: () => {
        dispatch(trackTicketOptionsCardExpand(trip_id, card_name));
      }
    };
  }

  const selected_trip = trip_options_matrix
    .flat()
    .find(m => m.trip_id === active_trip_id);

  return (
    <Stack direction="column" width="100%" justifyContent="space-between">
      <Stack direction="column" minHeight="100vh" flex="1">
        <Stack
          justifyContent="space-between"
          alignItems="center"
          className={classes.header}
        >
          <Heading size="sm">
            {translator.t("!results.classes-fares.review-ticket-options")}
          </Heading>
          <IconButton
            onClick={onClose}
            color="tertiary"
            variant="text"
            size="sm"
          >
            <CrossMD />
          </IconButton>
        </Stack>
        {!!default_trip_id && (
          <div className={classes.urgencyBannerParent}>
            <UrgencyAlert
              result_search_count={result_search_count}
              has_results={true}
              has_alternatives={true}
              error_type={null}
              inventory_ready={inventory_ready}
            />
          </div>
        )}

        <PriceLockSmokeTest price_in_cents={selected_trip?.price_in_cents} />

        <ExpandableCardsAccordion
          // The key here lets us cleanly replace the accordion if the default collapse/expand state(s) needs to change
          key={`${default_trip_id}-${is_single_class_option}-${is_single_fare_option}`}
          borderRadius="xl"
          boxShadow="unset"
          classes={{ root: classes.content }}
          card_header_classes_override={classes.cardHeader}
        >
          {!!operator_has_ratings && (
            <ExpandableCardContent
              is_expanded={!features.COLLAPSE_OPERATOR_REVIEWS}
              header={<OperatorRatingsCardHeader trip_id={active_trip_id} />}
              wrapper_classes={classes.cardContent}
              show_divider={false}
              hidden={!features.SHOW_REVIEWS_FOR_TRIP_DETAILS}
              {...generateTrackingEvents(active_trip_id, REVIEWS_CARD_NAME)}
            >
              <CardContent className={classes.innerCardContent}>
                <OperatorRatings trip_id={active_trip_id} />
              </CardContent>
            </ExpandableCardContent>
          )}

          {unique_destinations.length > 1 && (
            <ExpandableCardContent
              key={SHUTTLE_DESTINATIONS_CARD_NAME}
              is_expanded={true}
              header={translator.t("!results.shuttle-card.select-destination")}
              wrapper_classes={classes.cardContent}
              show_divider={false}
              {...generateTrackingEvents(
                active_trip_id,
                SHUTTLE_DESTINATIONS_CARD_NAME
              )}
            >
              <CardContent>
                <Select
                  labelText={translator.t("!search.input.destination.label")}
                  value={
                    journey_id ??
                    unique_destinations[unique_destinations.length - 1]
                      ?.journey_id
                  }
                  onChange={e => {
                    selectTripId(undefined);
                    setJourneyId(e.target.value);
                  }}
                >
                  {unique_destinations.map(
                    ({ journey_id, destination_name }) => (
                      <Select.Option key={journey_id} value={journey_id}>
                        {destination_name}
                      </Select.Option>
                    )
                  )}
                </Select>
              </CardContent>
            </ExpandableCardContent>
          )}
          <ExpandableCardContent
            key={ITINERARY_CARD_NAME}
            is_expanded={is_itinerary_block_expanded}
            header={translator.t("!departure.stops-and-transfers.title")}
            wrapper_classes={classes.cardContent}
            show_divider={false}
            {...generateTrackingEvents(active_trip_id, ITINERARY_CARD_NAME)}
          >
            <CardContent className={classes.innerCardContent}>
              {!active_trip_id ? (
                renderLoader()
              ) : (
                <Itinerary
                  trip_id={active_trip_id}
                  renderLoader={renderLoader}
                />
              )}
            </CardContent>
          </ExpandableCardContent>
          {!is_amenities_selection_hidden && (
            <ExpandableCardContent
              key={AMENITIES_CARD_NAME}
              is_expanded={is_comfort_block_expanded}
              header={translator.t("!results.classes-fares.comfort-options")}
              wrapper_classes={classes.cardContent}
              show_divider={false}
              {...generateTrackingEvents(active_trip_id, AMENITIES_CARD_NAME)}
            >
              <CardContent className={classes.innerCardContent}>
                <ClassesList
                  onSelect={selectTripId}
                  selected_trip_id={active_trip_id}
                  leg_card_id={leg_card_id}
                  journey_id={journey_id}
                />
              </CardContent>
            </ExpandableCardContent>
          )}
          <ExpandableCardContent
            key={CLASSES_AND_FARES_CARD_NAME}
            is_expanded
            header={translator.t(
              "!results.classes-fares.flexibility-conditions"
            )}
            wrapper_classes={classes.cardContent}
            show_divider={false}
            {...generateTrackingEvents(
              active_trip_id,
              CLASSES_AND_FARES_CARD_NAME
            )}
          >
            <CardContent className={classes.innerCardContent}>
              <FaresList
                leg_card_id={leg_card_id}
                selectTripId={selectTripId}
                selected_trip_id={active_trip_id}
                journey_id={journey_id}
                loading={!active_trip_id}
              />
            </CardContent>
          </ExpandableCardContent>
        </ExpandableCardsAccordion>
      </Stack>
      <div className={classes.buttonContainer}>
        <BottomSection
          is_external={is_external}
          selected_trip={selected_trip}
          is_vehicle_loading={is_loading_vehicle}
          is_vehicle_available={is_vehicle_available}
          trip_options_matrix={trip_options_matrix}
        />
      </div>
    </Stack>
  );
};
